// Billing — live revenue / outstanding view + payment workflow + receipt.
//
// Pulls every reservation, computes the live balance per row, and lets the
// front desk record a payment against any reservation with a balance.
// On successful payment, a receipt drawer appears that the operator can
// print (or save as PDF) immediately.  Cash + Mobile Money are switchable
// at the point-of-sale; the payment gateway integration will plug into
// the same `submitPayment` handler later.
//
// No backend changes — uses the existing
//   POST /reservations/:id/payments
//   GET  /reservations/:id          (reservation + payments[])
//   GET  /reservations              (full list for the table)
// endpoints.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  reservationsUrl, reservationByIdUrl, reservationPaymentsUrl,
} from "../../BaseURL";
import { useFacility } from "../../context/FacilityContext";
// Shared printable receipt — see components/Receipt.js.  Same dialog is
// now reused by CheckInOut so walk-in deposits and checkout payments
// both surface a "Print Receipt" affordance.
import { ReceiptDialog, openReceiptFor } from "../../components/Receipt";

// Payment methods.  "Cash" is live; the others map to gateways we'll
// integrate later (Momo / card processor).  Each row carries an `enabled`
// flag so we can ship the UI now and flip them on as integrations land.
const PAYMENT_METHODS = [
  { id: "Cash",         label: "Cash",          icon: "💵", enabled: true,  hint: "Counter cash payment" },
  { id: "Mobile Money", label: "Mobile Money",  icon: "📱", enabled: false, hint: "MTN / Vodafone / AirtelTigo (gateway coming)" },
  { id: "Card",         label: "Card",          icon: "💳", enabled: false, hint: "Visa / Mastercard (gateway coming)" },
  { id: "Transfer",     label: "Bank Transfer", icon: "🏦", enabled: true,  hint: "Manual confirmation" },
];

const FILTERS = [
  { id: "outstanding", label: "Outstanding only" },
  { id: "in_house",    label: "In-house" },
  { id: "checked_out", label: "Checked out" },
  { id: "all",         label: "All" },
];

export default function Billing() {
  const { facility } = useFacility();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("outstanding");
  const [msg, setMsg] = useState({ type: "", text: "" });

  // Workflow state.
  const [paymentFor, setPaymentFor] = useState(null);     // reservation
  const [receipt, setReceipt]       = useState(null);     // { reservation, payment }

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 5000);
  };

  // ── Load ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(reservationsUrl);
      setReservations(res.data?.results || []);
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Could not load reservations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = reservations;
    if (filter === "outstanding") list = list.filter(r => Number(r.balance) > 0 && r.status !== "cancelled");
    else if (filter === "in_house") list = list.filter(r => r.status === "checked_in");
    else if (filter === "checked_out") list = list.filter(r => r.status === "checked_out");
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(r =>
        (r.code || "").toLowerCase().includes(term) ||
        (r.guest_name || "").toLowerCase().includes(term) ||
        String(r.room_number || "").toLowerCase().includes(term)
      );
    }
    return list;
  }, [reservations, filter, search]);

  const totals = useMemo(() => {
    const settled    = reservations.reduce((s, r) => s + (Number(r.paid_amount)  || 0), 0);
    const booked     = reservations.reduce((s, r) => s + (Number(r.total_amount) || 0), 0);
    const outstanding = reservations
      .filter(r => r.status !== "cancelled")
      .reduce((s, r) => s + (Number(r.balance) || 0), 0);
    const dueOut = reservations.filter(r => r.status === "checked_in" && Number(r.balance) > 0).length;
    return { settled, booked, outstanding, dueOut };
  }, [reservations]);

  // ── Payment submit ────────────────────────────────────────────────────
  // Called by the PaymentDialog with the typed amount + method.  Records
  // the payment, then opens the receipt drawer with the freshly-saved
  // reservation snapshot.
  const submitPayment = async ({ amount, method, reference, notes }) => {
    if (!paymentFor) return;
    const res = await axios.post(reservationPaymentsUrl(paymentFor.id), {
      amount: Number(amount), method, reference: reference || undefined, notes: notes || undefined,
    });
    if (!res.data?.success) {
      throw new Error(res.data?.message || "Payment failed");
    }
    // Re-fetch the reservation so we have the freshest balance + the full
    // payment row (incl. server-generated id and received_at).
    const detail = await axios.get(reservationByIdUrl(paymentFor.id));
    const reservation = detail.data?.reservation;
    const payments    = detail.data?.payments || [];
    const lastPayment = payments[payments.length - 1] || {
      amount: Number(amount), method, reference, notes, received_at: new Date().toISOString(),
    };
    setReceipt({ reservation, payment: lastPayment, allPayments: payments });
    setPaymentFor(null);
    showMsg("success", `Payment recorded — GH₵ ${Number(amount).toFixed(2)} (${method}).`);
    await load();
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, color: "#2c1f14", fontWeight: 500, marginBottom: 4 }}>
          Billing & Payments
        </div>
        <div style={{ fontSize: 14.5, color: "#6b5142" }}>
          Live balances, point-of-sale payments and printable receipts.
        </div>
      </div>

      {/* KPI row */}
      <div className="g4" style={{ marginBottom: 22 }}>
        <Stat icon="💵" label="Booked Total"  value={fmtMoney(totals.booked)}     sub="All reservations" />
        <Stat icon="✅" label="Collected"     value={fmtMoney(totals.settled)}    sub="To date" accent="#4a7c59" />
        <Stat icon="⚠️" label="Outstanding"   value={fmtMoney(totals.outstanding)} sub={`${reservations.filter(r => Number(r.balance) > 0 && r.status !== "cancelled").length} bills with a balance`} accent="#a93226" />
        <Stat icon="🛏️" label="In-house · Owing" value={totals.dueOut} sub="Checked-in guests with a balance" accent="#3a7bd5" />
      </div>

      {/* Toast */}
      {msg.text && (
        <div style={{
          padding: "11px 16px", borderRadius: 8, marginBottom: 14,
          background: msg.type === "success" ? "#eaf5ee" : "#fef0ee",
          color:      msg.type === "success" ? "#2d6a4f" : "#a93226",
          border:     `1px solid ${msg.type === "success" ? "#a8d5b5" : "#f5c5bc"}`,
          fontSize: 14, fontWeight: 500,
        }}>
          {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {FILTERS.map(f => (
          <button key={f.id} className={`btn-outline ${filter === f.id ? "active" : ""}`}
            onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
        <input
          className="f-input"
          style={{ maxWidth: 280, padding: "9px 12px", marginLeft: 8 }}
          placeholder="🔍  Search code, guest or room…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Bill list */}
      <div className="panel">
        <div className="panel-hd">
          <span className="panel-hd-title">Bills</span>
          <span className="badge badge-gold">
            {loading ? "…" : `${filtered.length} record${filtered.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th><th>Guest</th><th>Room</th><th>Stay</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Paid</th>
                <th style={{ textAlign: "right" }}>Balance</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 28, color: "#6b5142" }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 28, color: "#6b5142", fontStyle: "italic" }}>
                  {filter === "outstanding" ? "✓ No outstanding balances. Everyone is settled." : "No bills match these filters."}
                </td></tr>
              ) : filtered.map(r => {
                const balance = Number(r.balance) || 0;
                const isPaid  = balance <= 0;
                return (
                  <tr key={r.id}>
                    <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16 }}>{r.code}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.guest_name}</div>
                      {r.guest_phone && <div style={{ fontSize: 12, color: "#8a7060" }}>{r.guest_phone}</div>}
                    </td>
                    <td>{r.room_number || "—"}</td>
                    <td style={{ fontSize: 12, color: "#6b5142" }}>
                      {fmtDate(r.check_in_at || r.check_in_date)} → {fmtDate(r.check_out_at || r.check_out_date)}
                    </td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.total_amount)}</td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.paid_amount)}</td>
                    <td style={{ textAlign: "right", color: isPaid ? "#2d6a4f" : "#a93226", fontWeight: 600 }}>
                      {fmtMoney(balance)}
                    </td>
                    <td><PaymentStatusPill status={r.payment_status} /></td>
                    <td style={{ textAlign: "right" }}>
                      {isPaid ? (
                        <button className="btn-outline" style={{ padding: "6px 12px", fontSize: 11 }}
                          onClick={() => openReceiptFor(r, setReceipt)}>
                          Receipt
                        </button>
                      ) : (
                        <button className="btn-dark" style={{ padding: "6px 14px", fontSize: 11 }}
                          onClick={() => setPaymentFor(r)}>
                          Take payment
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment dialog */}
      {paymentFor && (
        <PaymentDialog
          reservation={paymentFor}
          onClose={() => setPaymentFor(null)}
          onSubmit={submitPayment}
          showMsg={showMsg}
        />
      )}

      {/* Receipt drawer */}
      {receipt && (
        <ReceiptDialog
          reservation={receipt.reservation}
          payment={receipt.payment}
          allPayments={receipt.allPayments}
          facility={facility}
          onClose={() => setReceipt(null)}
        />
      )}
    </div>
  );
}

// ─── Payment dialog ─────────────────────────────────────────────────────
function PaymentDialog({ reservation, onClose, onSubmit, showMsg }) {
  const balance = Number(reservation.balance) || 0;
  const [amount, setAmount]     = useState(balance > 0 ? balance.toFixed(2) : "");
  const [method, setMethod]     = useState("Cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes]       = useState("");
  const [busy, setBusy]         = useState(false);

  const amt = Number(amount) || 0;
  const after = Math.max(0, balance - amt);
  const overpay = amt > balance;

  const onPay = async (e) => {
    e?.preventDefault?.();
    if (amt <= 0) { showMsg("error", "Enter an amount greater than 0."); return; }
    if (!method) { showMsg("error", "Pick a payment method."); return; }
    setBusy(true);
    try {
      await onSubmit({ amount: amt, method, reference, notes });
    } catch (err) {
      showMsg("error", err.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-box" style={{ width: "min(540px, 96vw)" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Take Payment — {reservation.code}</span>
          <button className="modal-close" onClick={() => !busy && onClose()}>✕</button>
        </div>
        <form className="modal-body" onSubmit={onPay}>
          {/* Bill summary */}
          <div style={{
            padding: "14px 16px", borderRadius: 10,
            background: "linear-gradient(135deg, rgba(200,160,90,.10), rgba(200,160,90,.04))",
            border: "1px solid rgba(200,160,90,.3)",
            marginBottom: 18,
          }}>
            <div style={{ fontSize: 13, color: "#6b5142", marginBottom: 8 }}>
              <strong style={{ color: "#2c1f14" }}>{reservation.guest_name}</strong>
              {reservation.guest_phone ? ` · ${reservation.guest_phone}` : ""}
              {reservation.room_number ? ` · Room ${reservation.room_number}` : ""}
            </div>
            <BillLine label="Booking total" value={fmtMoney(reservation.total_amount)} />
            <BillLine label="Paid so far"   value={fmtMoney(reservation.paid_amount)} />
            <BillLine label="Balance due"   value={fmtMoney(balance)} bold danger={balance > 0} />
          </div>

          {/* Method picker */}
          <div className="f-group">
            <label className="f-label">Payment Method *</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {PAYMENT_METHODS.map(m => {
                const on = method === m.id;
                return (
                  <button key={m.id} type="button"
                    disabled={!m.enabled}
                    title={m.enabled ? m.hint : `${m.hint} — not yet enabled`}
                    onClick={() => m.enabled && setMethod(m.id)}
                    style={{
                      textAlign: "left", padding: "12px 14px",
                      borderRadius: 9, cursor: m.enabled ? "pointer" : "not-allowed",
                      background: on ? "linear-gradient(135deg, #c8a05a, #a8814a)" : "transparent",
                      border: `1.5px solid ${on ? "#a8814a" : "rgba(180,140,80,.3)"}`,
                      color: on ? "#1a110a" : "#3a2a1a",
                      opacity: m.enabled ? 1 : .55,
                      fontFamily: "'Jost', sans-serif",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{m.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: ".04em" }}>{m.label}</span>
                      {!m.enabled && (
                        <span style={{ marginLeft: "auto", fontSize: 9, letterSpacing: ".1em",
                          textTransform: "uppercase", color: on ? "#1a110a" : "#8a6a30" }}>Soon</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 3, color: on ? "rgba(26,17,10,.7)" : "#6b5142" }}>
                      {m.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount + Reference */}
          <div className="form-grid">
            <div className="f-group">
              <label className="f-label">Amount (GH₵) *</label>
              <input className="f-input" type="number" min="0" step="0.01" required autoFocus
                value={amount}
                onChange={(e) => setAmount(e.target.value)}/>
              {balance > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                  <SmallPill label="Full" onClick={() => setAmount(balance.toFixed(2))} />
                  <SmallPill label="Half" onClick={() => setAmount((balance / 2).toFixed(2))} />
                </div>
              )}
            </div>
            <div className="f-group">
              <label className="f-label">Reference</label>
              <input className="f-input"
                placeholder={method === "Cash" ? "Optional" : "Txn ID / cheque #"}
                value={reference}
                onChange={(e) => setReference(e.target.value)}/>
            </div>
          </div>

          <div className="f-group">
            <label className="f-label">Notes</label>
            <input className="f-input"
              placeholder="Optional context for this payment"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}/>
          </div>

          {/* After-payment preview */}
          <div style={{
            padding: "10px 14px", borderRadius: 8,
            background: overpay ? "rgba(232,178,80,.10)" : "rgba(74,124,89,.08)",
            border:     `1px solid ${overpay ? "rgba(232,178,80,.4)" : "rgba(74,124,89,.3)"}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 14, fontSize: 13,
          }}>
            <span style={{ color: "#3a2a1a" }}>
              After this payment, balance will be{" "}
              <strong style={{ color: overpay ? "#a85618" : "#2d6a4f" }}>{fmtMoney(after)}</strong>
            </span>
            {overpay && (
              <span style={{ fontSize: 11, color: "#a85618", fontWeight: 500 }}>
                ⚠ Overpayment by {fmtMoney(amt - balance)}
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button type="submit" className="btn-dark" style={{ flex: 1 }} disabled={busy}>
              {busy ? "Recording…" : `Record ${fmtMoney(amt)}`}
            </button>
            <button type="button" className="btn-outline" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SmallPill({ label, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        padding: "3px 10px", borderRadius: 999, fontSize: 11,
        background: "rgba(180,140,80,.12)", border: "1px solid rgba(180,140,80,.25)",
        color: "#6b5142", cursor: "pointer", fontFamily: "'Jost', sans-serif",
      }}>{label}</button>
  );
}

// Receipt dialog + printable body now live in components/Receipt.js
// (imported above) so CheckInOut walk-in / checkout can reuse them.

// ─── Tiny presentation helpers ──────────────────────────────────────────
function Stat({ icon, label, value, sub, accent }) {
  return (
    <div className="stat" style={accent ? { borderLeft: `3px solid ${accent}` } : null}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-val">{value}</div>
      <div className="stat-lbl">{label}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

function BillLine({ label, value, bold, danger }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      padding: "2px 0",
      fontSize: bold ? 14 : 13,
      fontWeight: bold ? 600 : 400,
      color: danger ? "#a93226" : "#3a2a1a",
      borderTop: bold ? "1px solid rgba(180,140,80,.25)" : "none",
      marginTop: bold ? 6 : 0,
      paddingTop: bold ? 8 : 2,
    }}>
      <span>{label}</span><span>{value}</span>
    </div>
  );
}

// Row / Divider / ReceiptLabel + the th/td styles moved into the shared
// components/Receipt.js along with the receipt body.

const PAY_STATUS_COLOR = {
  unpaid:   { bg: "rgba(192,57,43,.10)",  fg: "#a93226" },
  partial:  { bg: "rgba(200,160,90,.14)", fg: "#8a6a30" },
  paid:     { bg: "rgba(74,124,89,.14)",  fg: "#2d6a4f" },
  refunded: { bg: "rgba(122,74,74,.12)",  fg: "#6a3a3a" },
};
function PaymentStatusPill({ status }) {
  const c = PAY_STATUS_COLOR[status] || { bg: "rgba(180,140,80,.10)", fg: "#6b5142" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 999,
      background: c.bg, color: c.fg,
      fontSize: 10.5, fontWeight: 500, letterSpacing: ".08em", textTransform: "uppercase",
    }}>{status}</span>
  );
}

const fmtMoney = (n) => `GH₵ ${Number(n || 0).toFixed(2)}`;
const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) => iso
  ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";
